import React, { useMemo } from 'react';
import { useTranslation } from "react-i18next";
import type { TFunction } from 'i18next';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Input, Label, Button, Alert, AlertDescription } from '../../ui';
import { GripVertical, Plus, Trash2, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { HelpCenterLink } from '../../../types';

interface HelpCenterLinksSettingsProps {
  headerLinks: HelpCenterLink[];
  footerLinks: HelpCenterLink[];
  isProCloudPlan: boolean;
  onUpdate: (headerLinks: HelpCenterLink[], footerLinks: HelpCenterLink[]) => Promise<void>;
}

const MAX_TITLE_LENGTH = 50;
const MAX_URL_LENGTH = 255;

// Zod schema for validation
const createLinkSchema = (t: TFunction) => z.object({
  id: z.string(),
  title: z.string()
    .min(1, t('helpCenter.linksSettings.titleRequired'))
    .max(MAX_TITLE_LENGTH, t('helpCenter.linksSettings.titleMaxLength', { max: MAX_TITLE_LENGTH })),
  url: z.string()
    .min(1, t('helpCenter.linksSettings.urlRequired'))
    .max(MAX_URL_LENGTH, t('helpCenter.linksSettings.urlMaxLength', { max: MAX_URL_LENGTH }))
    .refine(
      (val) => {
        try {
          new URL(val);
          return true;
        } catch {
          return val.startsWith('/') || val.startsWith('#');
        }
      },
      t('helpCenter.linksSettings.urlInvalid')
    ),
  order: z.number(),
});

const createFormSchema = (t: TFunction) => z.object({
  headerLinks: z.array(createLinkSchema(t)),
  footerLinks: z.array(createLinkSchema(t)),
});

type FormData = z.infer<ReturnType<typeof createFormSchema>>;

interface SortableLinkItemProps {
  link: HelpCenterLink;
  index: number;
  fieldName: 'headerLinks' | 'footerLinks';
  control: any;
  onDelete: () => void;
  sortableId: string; // Add explicit sortableId prop
}

function SortableLinkItem({ link, index, fieldName, control, onDelete, sortableId }: SortableLinkItemProps) {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId });

  const titleFieldName = `${fieldName}.${index}.title` as const;
  const urlFieldName = `${fieldName}.${index}.url` as const;

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className="flex items-start gap-3 p-3 border rounded-lg bg-card hover:bg-accent/50 transition-colors"
    >
      <button
        {...attributes}
        {...listeners}
        className="mt-2 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
        type="button"
        aria-label={t('helpCenter.linksSettings.dragHandle')}
      >
        <GripVertical className="h-5 w-5" />
      </button>
      <div className="flex-1 space-y-2">
        <div>
          <Label htmlFor={`title-${link.id}`} className="text-xs text-muted-foreground">
            {t('helpCenter.linksSettings.titleLabel')}
          </Label>
          <Controller
            name={titleFieldName}
            control={control}
            render={({ field, fieldState }) => (
              <>
                <Input
                  {...field}
                  id={`title-${link.id}`}
                  placeholder={t('helpCenter.linksSettings.titlePlaceholder')}
                  maxLength={MAX_TITLE_LENGTH}
                  className={fieldState.error ? 'border-destructive' : ''}
                />
                {fieldState.error && (
                  <p className="text-xs text-destructive mt-1">{fieldState.error.message}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {field.value?.length || 0}/{MAX_TITLE_LENGTH}
                </p>
              </>
            )}
          />
        </div>
        <div>
          <Label htmlFor={`url-${link.id}`} className="text-xs text-muted-foreground">
            {t('helpCenter.linksSettings.urlLabel')}
          </Label>
          <Controller
            name={urlFieldName}
            control={control}
            render={({ field, fieldState }) => (
              <>
                <Input
                  {...field}
                  id={`url-${link.id}`}
                  placeholder={t('helpCenter.linksSettings.urlPlaceholder')}
                  maxLength={MAX_URL_LENGTH}
                  className={fieldState.error ? 'border-destructive' : ''}
                />
                {fieldState.error && (
                  <p className="text-xs text-destructive mt-1">{fieldState.error.message}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {field.value?.length || 0}/{MAX_URL_LENGTH}
                </p>
              </>
            )}
          />
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={onDelete}
        className="mt-2 text-destructive hover:text-destructive"
        type="button"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

export const HelpCenterLinksSettings: React.FC<HelpCenterLinksSettingsProps> = ({
  headerLinks: initialHeaderLinks,
  footerLinks: initialFooterLinks,
  isProCloudPlan,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const formSchema = useMemo(() => createFormSchema(t), [t]);
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      headerLinks: initialHeaderLinks || [],
      footerLinks: initialFooterLinks || [],
    },
    mode: 'onBlur', // Validate on blur for better UX
  });

  const {
    fields: headerFields,
    append: appendHeader,
    remove: removeHeader,
    replace: replaceHeader,
  } = useFieldArray({
    control: form.control,
    name: 'headerLinks',
  });

  const {
    fields: footerFields,
    append: appendFooter,
    remove: removeFooter,
    replace: replaceFooter,
  } = useFieldArray({
    control: form.control,
    name: 'footerLinks',
  });


  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Require 5px of movement before activating drag (prevents accidental drags from inputs)
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const generateId = () => `link-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const handleAddLink = (type: 'header' | 'footer') => {
    const newLink: HelpCenterLink = {
      id: generateId(),
      title: '',
      url: '',
      order: type === 'header' ? headerFields.length : footerFields.length,
    };

    if (type === 'header') {
      appendHeader(newLink);
    } else {
      appendFooter(newLink);
    }
  };

  const handleDragEnd = (event: DragEndEvent, type: 'header' | 'footer') => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const fields = type === 'header' ? headerFields : footerFields;
    const replace = type === 'header' ? replaceHeader : replaceFooter;
    const fieldName = type === 'header' ? 'headerLinks' : 'footerLinks';

    const oldIndex = fields.findIndex((field) => field.id === active.id);
    const newIndex = fields.findIndex((field) => field.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
      // Get current form values BEFORE any changes
      const currentValues = form.getValues(fieldName) as HelpCenterLink[];
      
      if (!currentValues || currentValues.length === 0) {
        console.warn('No form values found, skipping reorder');
        return;
      }
      
      // Ensure we have the same number of values as fields
      if (currentValues.length !== fields.length) {
        console.warn(`Mismatch: ${currentValues.length} values vs ${fields.length} fields`);
        return;
      }
      
      // Manually reorder the array
      const reordered = [...currentValues];
      const [movedItem] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, movedItem);
      
      // Update order property to match new indices
      const withOrder = reordered.map((link: HelpCenterLink, index: number) => ({ 
        ...link, 
        order: index 
      }));
      
      // Use replace() to replace the entire array at once
      // This ensures both the field array and form values are updated together
      replace(withOrder);
    }
  };

  const onSubmit = async (data: FormData) => {
    try {
      // Ensure order is correct
      const headerLinks = data.headerLinks.map((link, index) => ({ ...link, order: index }));
      const footerLinks = data.footerLinks.map((link, index) => ({ ...link, order: index }));
      
      await onUpdate(headerLinks, footerLinks);
      toast.success(t('helpCenter.linksSettings.saveSuccess'), { position: 'top-center', duration: 2000 });
    } catch (error) {
      console.error('Error saving links:', error);
      toast.error(t('helpCenter.linksSettings.saveFailed'), { position: 'top-center', duration: 3000 });
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isValid = await form.trigger(); // Trigger validation for all fields
    
    if (!isValid) {
      // Count invalid links
      const headerErrors = form.formState.errors.headerLinks ?? [];
      const footerErrors = form.formState.errors.footerLinks ?? [];
      const invalidHeaderCount = Array.isArray(headerErrors) ? headerErrors.filter(Boolean).length : 0;
      const invalidFooterCount = Array.isArray(footerErrors) ? footerErrors.filter(Boolean).length : 0;
      
      if (invalidHeaderCount > 0 || invalidFooterCount > 0) {
        const invalidTypes = [];
        if (invalidHeaderCount > 0) {
          invalidTypes.push(t('helpCenter.linksSettings.headerLinkCount', { count: invalidHeaderCount }));
        }
        if (invalidFooterCount > 0) {
          invalidTypes.push(t('helpCenter.linksSettings.footerLinkCount', { count: invalidFooterCount }));
        }

        toast.error(
          t('helpCenter.linksSettings.fixLinksError', { types: invalidTypes.join(t('helpCenter.linksSettings.listSeparator')) }),
          { position: 'top-center', duration: 5000 }
        );
      }
      return;
    }
    
    form.handleSubmit(onSubmit)(e);
  };

  const renderLinksSection = (
    title: string,
    description: string,
    fields: Array<{ id: string }>,
    type: 'header' | 'footer'
  ) => {
    const remove = type === 'header' ? removeHeader : removeFooter;
    const fieldName = type === 'header' ? 'headerLinks' : 'footerLinks';

    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold">{title}</h3>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleAddLink(type)}
            type="button"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('helpCenter.linksSettings.addLink')}
          </Button>
        </div>

        {fields.length === 0 ? (
          <div className="text-center py-8 border border-dashed rounded-lg text-muted-foreground">
            <p className="text-sm">{t('helpCenter.linksSettings.noLinks', { type })}</p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(e: DragEndEvent) => handleDragEnd(e, type)}
          >
            <SortableContext
              items={fields.map(field => field.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {fields.map((field, index) => {
                  const link = form.watch(`${fieldName}.${index}`) as HelpCenterLink;
                  const fieldErrors = form.formState.errors[fieldName]?.[index] as 
                    | { title?: { message?: string }; url?: { message?: string } }
                    | undefined;
                  const isInvalid = !!(fieldErrors?.title || fieldErrors?.url);
                  
                  return (
                    <div
                      key={field.id}
                      className={isInvalid ? 'border-destructive bg-destructive/5 rounded-lg' : ''}
                    >
                      <SortableLinkItem
                        link={link || (field as HelpCenterLink)}
                        index={index}
                        fieldName={fieldName}
                        control={form.control}
                        onDelete={() => remove(index)}
                        sortableId={field.id}
                      />
                    </div>
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    );
  };

  if (!isProCloudPlan) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {t('helpCenter.linksSettings.proOnly')}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <form onSubmit={handleFormSubmit} className="space-y-6">
      {renderLinksSection(
        t('helpCenter.linksSettings.headerLinksTitle'),
        t('helpCenter.linksSettings.headerLinksDescription'),
        headerFields,
        'header'
      )}
      {renderLinksSection(
        t('helpCenter.linksSettings.footerLinksTitle'),
        t('helpCenter.linksSettings.footerLinksDescription'),
        footerFields,
        'footer'
      )}
      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={form.formState.isSubmitting}
          loading={form.formState.isSubmitting}
        >
          {t('helpCenter.linksSettings.saveLinks')}
        </Button>
      </div>
    </form>
  );
};
